import {startOfToday} from 'date-fns';
import {closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {usePatchCoreExchangeCustomerDeliveryMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, useGetUserOptions} from '@dms/shared';

import {getApiDateTimeString, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DialogActions} from './DialogActions';

const CustomerDeliveryFormSchema = z.object({
  deliveryDate: z.date(),
  receivedBy: z.string().min(1),
  storageNote: z.string(),
});

type CustomerDeliveryForm = z.infer<typeof CustomerDeliveryFormSchema>;

interface CustomerDeliveryDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  currentUserId: string;
  onSaved: VoidFunction;
}

export function CustomerDeliveryDialog(props: CustomerDeliveryDialogProps) {
  const [patchCustomerDelivery, {isLoading}] = usePatchCoreExchangeCustomerDeliveryMutation();
  const {userOptions, isLoading: areUsersLoading} = useGetUserOptions();

  const handleSubmit = async (values: CustomerDeliveryForm) => {
    await patchCustomerDelivery({
      coreExchangeId: props.coreExchangeId,
      body: {...values, deliveryDate: getApiDateTimeString(values.deliveryDate)},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<CustomerDeliveryForm>
      experimentalZodSchema={CustomerDeliveryFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        deliveryDate: startOfToday(),
        receivedBy: props.currentUserId,
        storageNote: '',
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="date"
            name="deliveryDate"
            label={i18n.t('entity.warehouse.labels.coreExchangeDeliveryDate')}
            isRequired
            data-testid={suffixTestId('inputs.deliveryDate', props)}
          />
          <FormField
            control={control}
            type="choice"
            name="receivedBy"
            label={i18n.t('entity.warehouse.labels.receivedBy')}
            options={userOptions}
            isLoading={areUsersLoading}
            isNotClearable
            isRequired
            menuInPortal
            data-testid={suffixTestId('inputs.receivedBy', props)}
          />
          <FormField
            control={control}
            type="textarea"
            name="storageNote"
            label={i18n.t('entity.warehouse.labels.note')}
            data-testid={suffixTestId('inputs.storageNote', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
